import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';

export interface SpadesLeaderboardEntry {
  id: number;
  userId: string;
  nickname: string;
  gameType: string;
  rating: number;
  gamesPlayed: number;
  gamesWon: number;
  gamesLost: number;
  lastPlayed: string;
}

export interface TierInfo {
  name: string;
  eloRange: string;
  percentile: string;
  colorClass: string;
  minElo: number;
}

@Component({
  selector: 'app-rankings',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './rankings.html',
  styleUrl: './rankings.scss',
})
export class Rankings implements OnInit {
  private http = inject(HttpClient);

  // Active game queue selection: 'spades' | 'hearts' | 'solitaire'
  activeQueue = signal<string>('spades');
  queues = signal<string[]>(['spades', 'backgammon', 'canasta']);

  // Search filter
  searchQuery = signal('');

  // Pagination states
  skip = signal<number>(0);
  limit = signal<number>(10);

  // Leaderboard data states
  leaderboard = signal<SpadesLeaderboardEntry[]>([]);
  leaderboardLoading = signal(true);

  // Info popup state
  showInfo = signal<boolean>(false);

  // Computed page status signals
  hasNextPage = computed(() => this.leaderboard().length === this.limit());
  hasPrevPage = computed(() => this.skip() > 0);

  // Personal user stats (Diamond tier)
  userStats = signal({
    name: 'Player One',
    avatarUrl: 'https://static.h5arena.com/images/avatars/Premium/180.jpg',
    rating: 2150,
    peakRating: 2210,
    rank: 142,
    tier: 'Diamond',
    nextTier: 'Elite',
    nextTierMinElo: 2400,
    prevTierMinElo: 2000,
    winRate: 64,
    winLoss: '96W - 54L'
  });

  // Tiers configuration
  tiers = signal<TierInfo[]>([
    { name: 'Elite', eloRange: '2400+', percentile: 'Top 0.5%', colorClass: 'tier-elite', minElo: 2400 },
    { name: 'Diamond', eloRange: '2000 - 2399', percentile: 'Top 2%', colorClass: 'tier-diamond', minElo: 2000 },
    { name: 'Platinum', eloRange: '1600 - 1999', percentile: 'Top 10%', colorClass: 'tier-platinum', minElo: 1600 },
    { name: 'Gold', eloRange: '1200 - 1599', percentile: 'Top 25%', colorClass: 'tier-gold', minElo: 1200 },
    { name: 'Silver', eloRange: '800 - 1199', percentile: 'Top 50%', colorClass: 'tier-silver', minElo: 800 },
    { name: 'Bronze', eloRange: '0 - 799', percentile: 'Bottom 50%', colorClass: 'tier-bronze', minElo: 0 }
  ]);

  // Computed filtered leaderboard based on query string
  filteredLeaderboard = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const data = this.leaderboard();
    if (!query) return data;
    return data.filter(entry => entry.nickname.toLowerCase().includes(query));
  });

  ngOnInit(): void {
    this.loadLeaderboard(this.activeQueue(), this.skip());
  }

  loadLeaderboard(game: string, skipAmount: number = 0) {
    this.leaderboardLoading.set(true);
    this.http.get<SpadesLeaderboardEntry[]>(`https://cards.api.rivalforge.com/api/stats/leaderboard/${game}?limit=10&skip=${skipAmount}`).subscribe({
      next: (data) => {
        const sorted = [...data].sort((a, b) => b.rating - a.rating);
        this.leaderboard.set(sorted);
        this.leaderboardLoading.set(false);
      },
      error: (err) => {
        console.error(`Failed to load ${game} leaderboard`, err);
        this.leaderboard.set([]);
        this.leaderboardLoading.set(false);
      }
    });
  }

  selectQueue(game: string) {
    this.activeQueue.set(game);
    this.skip.set(0);
    this.loadLeaderboard(game, 0);
  }

  nextPage() {
    if (this.hasNextPage()) {
      const newSkip = this.skip() + 10;
      this.skip.set(newSkip);
      this.loadLeaderboard(this.activeQueue(), newSkip);
    }
  }

  prevPage() {
    if (this.hasPrevPage()) {
      const newSkip = Math.max(0, this.skip() - 10);
      this.skip.set(newSkip);
      this.loadLeaderboard(this.activeQueue(), newSkip);
    }
  }

  getTierProgressPercent(): number {
    const stats = this.userStats();
    const currentElo = stats.rating;
    const minElo = stats.prevTierMinElo;
    const maxElo = stats.nextTierMinElo;

    if (currentElo >= maxElo) return 100;
    const totalNeeded = maxElo - minElo;
    const progress = currentElo - minElo;
    return Math.max(0, Math.min(100, Math.round((progress / totalNeeded) * 100)));
  }

  getTierColorClass(tierName: string): string {
    switch (tierName) {
      case 'Elite': return 'tier-elite';
      case 'Diamond': return 'tier-diamond';
      case 'Platinum': return 'tier-platinum';
      case 'Gold': return 'tier-gold';
      case 'Silver': return 'tier-silver';
      default: return 'tier-bronze';
    }
  }

  toggleInfo() {
    this.showInfo.update(val => !val);
  }
}
